import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Plus, ClipboardList, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { api, errMsg, toList } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Empty, Btn, shortDate } from "../../components/app/kit";
import { Modal, Field, TextInput, Chips } from "../../components/app/Form";
import { BoxScore } from "../../components/app/BoxScore";
import { useRosterNames } from "../../hooks/useRosterNames";
import { FinalScoreShare } from "../../components/app/FinalScoreShare";
import { scoreOf, resultOf } from "./Overview";

const today = () => new Date().toLocaleDateString("en-CA");

export const AddGame = ({ open, onClose, tid, suffix = "" }) => {
  const navigate = useNavigate();
  const [f, setF] = useState({ opponent: "", date: today(), location: "", home_away: "home" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.opponent.trim()) return toast.error("Add the opponent");
    if (!f.date) return toast.error("Pick a date");
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${tid}/games`, { opponent: f.opponent.trim(), date: f.date, location: f.location.trim() || null, home_away: f.home_away });
      navigate(`/coach/games/${data.id}/stats${suffix}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} testid="add-game-dialog" title="Add game" description="Log a game, then type in the box score.">
      <form onSubmit={submit} className="mt-2 space-y-4">
        <Field label="Opponent"><TextInput data-testid="game-opponent-input" value={f.opponent} onChange={set("opponent")} placeholder="Lakewood Spartans" autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><TextInput data-testid="game-date-input" type="date" value={f.date} onChange={set("date")} /></Field>
          <Field label="Site"><Chips testid="game-site" options={[["home", "Home"], ["away", "Away"]]} value={f.home_away} onChange={set("home_away")} /></Field>
        </div>
        <Field label="Location"><TextInput data-testid="game-location-input" value={f.location} onChange={set("location")} placeholder="Home gym" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={onClose} data-testid="add-game-cancel">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="add-game-save">{busy ? "Creating…" : "Next: enter stats"}</Btn>
        </div>
      </form>
    </Modal>
  );
};

export function Games() {
  const { membership, canEdit } = useAuth();
  const tid = membership?.team_id;
  const { data, loading, error } = useApi(tid && `/teams/${tid}/games`);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const games = toList(data, ["games"]);
  return (
    <div>
      <PageHeader eyebrow={membership?.team_name} title="Games">
        {canEdit && <Btn data-testid="add-game-button" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />Add game</Btn>}
      </PageHeader>
      {canEdit && <AddGame open={adding} onClose={() => setAdding(false)} tid={tid} />}
      {loading ? <Loader /> : error ? <ErrorNote error={error} /> : !games.length ? <Empty title="No games logged" /> : (
        <div className="space-y-3">
          {games.map((g, i) => {
            const { us, them } = scoreOf(g);
            const r = resultOf(g);
            return (
              <div key={g.id ?? i} className="flex items-center gap-3 rounded-2xl border border-[#1E293B] bg-[#0F172A] pr-4 transition-colors hover:border-[#39FF14]/60">
              <Link to={`/coach/games/${g.id}`} data-testid={`game-row-${g.id}`} className="flex min-w-0 flex-1 items-center gap-4 px-5 py-4">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full font-display text-lg font-black ${r === "W" ? "bg-[#39FF14] text-[#0A0E1A]" : r === "L" ? "bg-[#FF3B30]/20 text-red-200" : "bg-[#162032] text-slate-300"}`}>{r}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-xl font-bold uppercase text-white">vs {g.opponent ?? "Opponent"}</p>
                  <p className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">{shortDate(g.date)}{g.location ? ` · ${g.location}` : ""}</p>
                </div>
                <span className="font-mono-gd text-2xl font-bold text-white">{us ?? "–"}<span className="text-slate-500">:</span>{them ?? "–"}</span>
              </Link>
              {canEdit && (
                <Btn variant="ghost" data-testid={`game-row-stats-${g.id}`} onClick={() => navigate(`/coach/games/${g.id}/stats`)} className="shrink-0 px-3 py-2 text-xs" aria-label="Enter stats">
                  <ClipboardList className="h-3.5 w-3.5" /><span className="hidden sm:inline">{g.lines && Object.keys(g.lines).length ? "Edit stats" : "Enter stats"}</span>
                </Btn>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function GameDetail() {
  const { gameId } = useParams();
  const { membership, canEdit, canRun } = useAuth();
  const navigate = useNavigate();
  const tid = membership?.team_id;
  const { data, loading, error } = useApi(tid && `/teams/${tid}/games/${gameId}`);
  const names = useRosterNames(tid);
  const roster = useApi(tid && `/teams/${tid}/players`);
  const g = data?.game ?? data ?? {};
  const { us, them } = scoreOf(g);
  const isFinal = String(g.status ?? "").toLowerCase() === "final" && us != null && them != null;
  const remove = async () => {
    if (!window.confirm(`Delete the game vs ${g.opponent ?? "Opponent"} and its stats?`)) return;
    try {
      await api.delete(`/teams/${tid}/games/${gameId}`);
      toast.success("Game deleted");
      navigate("/coach/games");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <Link to="/coach/games" data-testid="back-to-games" className="no-print mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#39FF14]"><ArrowLeft className="h-4 w-4" />Games</Link>
      {loading ? <Loader /> : error ? <ErrorNote error={error} /> : (
        <>
          <PageHeader eyebrow={shortDate(g.date)} title={`vs ${g.opponent ?? "Opponent"}`}>
            {canEdit && <Btn data-testid="enter-stats-button" onClick={() => navigate(`/coach/games/${gameId}/stats`)}><ClipboardList className="h-4 w-4" />{g.lines && Object.keys(g.lines).length ? "Edit stats" : "Enter stats"}</Btn>}
            <Btn variant="ghost" data-testid="print-boxscore-button" onClick={() => window.print()}><Printer className="h-4 w-4" />Print box score</Btn>
            {canRun && isFinal && <FinalScoreShare tid={tid} teamName={membership?.team_name} game={g} us={us} them={them} players={toList(roster.data, ["players"])} />}
            {canEdit && <Btn variant="danger" data-testid="delete-game-button" onClick={remove}><Trash2 className="h-4 w-4" />Delete</Btn>}
          </PageHeader>
          <Panel className="mb-6 flex items-center justify-around py-8">
            <div className="text-center"><p className="font-mono-gd text-xs uppercase tracking-widest text-slate-400">{membership?.team_name}</p><p data-testid="score-us" className="font-mono-gd text-6xl font-bold text-[#39FF14]">{us ?? "–"}</p></div>
            <span className="font-display text-3xl font-black text-slate-500">{g.status === "final" ? resultOf(g) : g.status ?? "—"}</span>
            <div className="text-center"><p className="font-mono-gd text-xs uppercase tracking-widest text-slate-400">{g.opponent ?? "Opponent"}</p><p data-testid="score-them" className="font-mono-gd text-6xl font-bold text-white">{them ?? "–"}</p></div>
          </Panel>
          <BoxScore lines={g.lines} names={names} />
        </>
      )}
    </div>
  );
}
